/**
 * AEOReadinessDashboard
 *
 * Centralized dashboard to assess site readiness for AEO content generation.
 * Tracks 11 dimensions with actionable buttons to fix gaps.
 *
 * Part of Story: AEO Readiness Dashboard
 * Created: 2026-02-07
 */

import { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Package,
  Search,
  Sparkles,
  HelpCircle,
  Workflow
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { ReadinessOverview, DimensionGrid, BlockersList, PipelineFlow, BatchEnrichmentPanel, ResearchCompleteness } from '@/components/aeo-readiness';

// Types
interface DimensionAction {
  label: string;
  url: string | null;
  inlineAction?: string;
}

interface DimensionData {
  score: number;
  status: 'green' | 'yellow' | 'red';
  metric: string;
  action: DimensionAction;
  [key: string]: unknown;
}

interface Blocker {
  severity: 'critical' | 'high' | 'medium';
  message: string;
  dimension: string;
}

interface AEOReadinessData {
  siteId: string;
  siteName: string;
  overallScore: number;
  status: 'ready' | 'mostly_ready' | 'needs_work' | 'not_ready';
  dimensions: {
    productsUploaded: DimensionData;
    productsCategorized: DimensionData;
    categoriesResearched: DimensionData;
    productsEnriched: DimensionData;
    suspiciousValues: DimensionData;
    categoryDemand: DimensionData;
    buyerPersonas: DimensionData;
    perceivedBenefits: DimensionData;
    embeddingsDone: DimensionData;
    questionWheels: DimensionData;
    aeoGenerated: DimensionData;
    attributeValueRanges: DimensionData;
  };
  blockers: Blocker[];
  analyzedAt: string;
}

// Processing status types
interface ProcessingStatus {
  isProcessing: boolean;
  enrichment: {
    totalBatches: number;
    completedBatches: number;
    activeBatches: number;
    failedBatches: number;
    percent: number;
  };
  embeddings: {
    totalBatches: number;
    completedBatches: number;
    activeBatches: number;
    percent: number;
  };
  summary: {
    totalProducts: number;
    enrichedProducts: number;
    embeddedProducts: number;
  };
}

export default function AEOReadinessDashboard() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AEOReadinessData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedSite) return;

    try {
      setError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${selectedSite.id}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load readiness data');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Failed to load AEO readiness:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedSite]);

  // Load processing status
  const loadProcessingStatus = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const response = await authFetch(`/api/aeo-readiness/site/${selectedSite.id}/processing-status`);
      if (response.ok) {
        const result = await response.json();
        setProcessingStatus(result.data);
      }
    } catch (err) {
      console.error('Failed to load processing status:', err);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite) {
      setLoading(true);
      loadData();
      loadProcessingStatus();
    }
  }, [selectedSite, loadData, loadProcessingStatus]);

  // Poll for processing status and refresh data when processing
  useEffect(() => {
    if (!processingStatus?.isProcessing) return;

    const interval = setInterval(() => {
      loadProcessingStatus();
      loadData(); // Also refresh the main data to update percentages
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [processingStatus?.isProcessing, loadProcessingStatus, loadData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view AEO readiness.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40" />
        <div className="grid grid-cols-6 gap-3">
          {[...Array(11)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={handleRefresh} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No data available.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Calculate group scores for tabs
  const productScore = Math.round(
    (data.dimensions.productsUploaded.score +
      data.dimensions.productsCategorized.score +
      data.dimensions.productsEnriched.score +
      data.dimensions.suspiciousValues.score) / 4
  );

  const researchScore = Math.round(
    (data.dimensions.categoriesResearched.score +
      data.dimensions.categoryDemand.score +
      data.dimensions.buyerPersonas.score +
      data.dimensions.perceivedBenefits.score) / 4
  );

  const contentScore = Math.round(
    (data.dimensions.embeddingsDone.score +
      data.dimensions.questionWheels.score +
      data.dimensions.aeoGenerated.score) / 3
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AEO Readiness Dashboard</h1>
          <p className="text-muted-foreground">
            Site readiness for AI Engine Optimization content generation
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Processing Status Banner */}
      {processingStatus?.isProcessing && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-800">Background Processing in Progress</h3>
                <p className="text-sm text-blue-600">
                  AI enrichment and embeddings are being generated. This page will auto-update.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <div className="flex justify-between text-xs text-blue-700 mb-1">
                      <span>Enrichment</span>
                      <span>
                        {processingStatus.enrichment.completedBatches}/{processingStatus.enrichment.totalBatches} batches
                        {processingStatus.enrichment.activeBatches > 0 && ` (${processingStatus.enrichment.activeBatches} active)`}
                      </span>
                    </div>
                    <Progress value={processingStatus.enrichment.percent} className="h-2" />
                    <div className="text-xs text-blue-600 mt-1">
                      {processingStatus.summary.enrichedProducts}/{processingStatus.summary.totalProducts} products ({processingStatus.enrichment.percent}%)
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-blue-700 mb-1">
                      <span>Embeddings</span>
                      <span>
                        {processingStatus.embeddings.completedBatches}/{processingStatus.embeddings.totalBatches} batches
                      </span>
                    </div>
                    <Progress value={processingStatus.embeddings.percent} className="h-2" />
                    <div className="text-xs text-blue-600 mt-1">
                      {processingStatus.summary.embeddedProducts}/{processingStatus.summary.totalProducts} products ({processingStatus.embeddings.percent}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall Score */}
      <ReadinessOverview
        overallScore={data.overallScore}
        status={data.status}
        blockers={data.blockers}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Products
            <Badge variant="secondary" className="ml-1 text-xs">
              {productScore}%
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="research" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Research
            <Badge variant="secondary" className="ml-1 text-xs">
              {researchScore}%
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Content
            <Badge variant="secondary" className="ml-1 text-xs">
              {contentScore}%
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <DimensionGrid
            dimensions={data.dimensions}
            siteId={selectedSite.id}
            onRefresh={handleRefresh}
          />

          <BlockersList
            blockers={data.blockers}
            dimensions={data.dimensions}
          />
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <PipelineFlow
            siteId={selectedSite.id}
            onRefresh={handleRefresh}
          />
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          {/* Batch Enrichment Panel - shown at top when enrichment is needed */}
          <BatchEnrichmentPanel siteId={selectedSite.id} onRefresh={handleRefresh} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DimensionDetailCard
              title="Products Uploaded"
              dimension={data.dimensions.productsUploaded}
              description="Products available in the feed for AEO generation"
              details={[
                { label: 'Total Products', value: (data.dimensions.productsUploaded.count as number) || 0 },
                { label: 'Data Size', value: `${(data.dimensions.productsUploaded.kbSize as number) || 0} KB` }
              ]}
            />

            <DimensionDetailCard
              title="Products Categorized"
              dimension={data.dimensions.productsCategorized}
              description="Products mapped to category taxonomy"
              details={[
                { label: 'Categorized', value: (data.dimensions.productsCategorized.categorized as number) || 0 },
                { label: 'Orphans', value: (data.dimensions.productsCategorized.orphanCount as number) || 0, highlight: true }
              ]}
            />

            <DimensionDetailCard
              title="Products AI Enriched"
              dimension={data.dimensions.productsEnriched}
              description="Products with AI-extracted attributes"
              details={[
                { label: 'Enriched', value: (data.dimensions.productsEnriched.enriched as number) || 0 },
                { label: 'Pending', value: ((data.dimensions.productsEnriched.total as number) || 0) - ((data.dimensions.productsEnriched.enriched as number) || 0) }
              ]}
            />

            <DimensionDetailCard
              title="Quality Issues"
              dimension={data.dimensions.suspiciousValues}
              description="Products flagged with data problems"
              details={[
                { label: 'Clean', value: ((data.dimensions.suspiciousValues.total as number) || 0) - ((data.dimensions.suspiciousValues.flagged as number) || 0) },
                { label: 'Flagged', value: (data.dimensions.suspiciousValues.flagged as number) || 0, highlight: true }
              ]}
            />
          </div>
        </TabsContent>

        {/* Research Tab */}
        <TabsContent value="research" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DimensionDetailCard
              title="Categories Researched"
              dimension={data.dimensions.categoriesResearched}
              description="Categories with canonical attributes defined"
              details={[
                { label: 'Researched', value: (data.dimensions.categoriesResearched.researchedCount as number) || 0 },
                { label: 'Total Categories', value: (data.dimensions.categoriesResearched.totalCount as number) || 0 }
              ]}
            />

            <DimensionDetailCard
              title="Category Demand"
              dimension={data.dimensions.categoryDemand}
              description="Categories with demand/search volume data"
              details={[
                { label: 'With Demand Data', value: (data.dimensions.categoryDemand.withDemand as number) || 0 },
                { label: 'Missing Data', value: ((data.dimensions.categoryDemand.total as number) || 0) - ((data.dimensions.categoryDemand.withDemand as number) || 0), highlight: true }
              ]}
            />

            <DimensionDetailCard
              title="Buyer Personas"
              dimension={data.dimensions.buyerPersonas}
              description="Personas defined for targeted content"
              details={[
                { label: 'Defined', value: (data.dimensions.buyerPersonas.count as number) || 0 },
                { label: 'Required', value: (data.dimensions.buyerPersonas.required as number) || 3 }
              ]}
            />

            <DimensionDetailCard
              title="Perceived Benefits"
              dimension={data.dimensions.perceivedBenefits}
              description="Customer benefits mapped to product attributes"
              details={[
                { label: 'Total Benefits', value: (data.dimensions.perceivedBenefits.count as number) || 0 },
                { label: 'Avg per Category', value: (data.dimensions.perceivedBenefits.avgPerCategory as number) || 0 }
              ]}
            />
          </div>

          {/* Research Completeness Matrix */}
          <ResearchCompleteness siteId={selectedSite.id} />
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DimensionDetailCard
              title="Embeddings Done"
              dimension={data.dimensions.embeddingsDone}
              description="Products with vector embeddings for semantic search"
              details={[
                { label: 'Embedded', value: (data.dimensions.embeddingsDone.embedded as number) || 0 },
                { label: 'Pending', value: ((data.dimensions.embeddingsDone.total as number) || 0) - ((data.dimensions.embeddingsDone.embedded as number) || 0), highlight: true }
              ]}
            />

            <DimensionDetailCard
              title="Question Wheels"
              dimension={data.dimensions.questionWheels}
              description="Question wheels evaluated for FAQ generation"
              details={[
                { label: 'Evaluated', value: (data.dimensions.questionWheels.evaluated as number) || 0 },
                { label: 'Total Wheels', value: (data.dimensions.questionWheels.total as number) || 0 }
              ]}
            />

            <DimensionDetailCard
              title="AEO Generated"
              dimension={data.dimensions.aeoGenerated}
              description="Products with AEO content generated"
              details={[
                { label: 'Generated', value: (data.dimensions.aeoGenerated.generated as number) || 0 },
                { label: 'Stale (>30d)', value: (data.dimensions.aeoGenerated.staleCount as number) || 0, highlight: true }
              ]}
            />

            {/* ⛔ THE 12TH DIMENSION. Without this card the only way it ever reached a human was as
                a blocker — so PARTIAL COVERAGE, the state an operator can actually act on, was
                invisible. The two counts are deliberately BOTH shown: `Categories covered` is
                site-scoped and `Sector total` is not, and a single number cannot say which. */}
            <DimensionDetailCard
              title="Attribute Value Ranges"
              dimension={data.dimensions.attributeValueRanges}
              description="Categories with value bands that say what counts as a good value for an attribute"
              details={[
                { label: 'Categories covered', value: (data.dimensions.attributeValueRanges.categoriesCovered as number) || 0 },
                {
                  label: 'Site categories',
                  value: (data.dimensions.attributeValueRanges.totalCategories as number) || 0,
                  highlight: ((data.dimensions.attributeValueRanges.categoriesCovered as number) || 0) <
                             ((data.dimensions.attributeValueRanges.totalCategories as number) || 0)
                },
                { label: 'Rows in sector', value: (data.dimensions.attributeValueRanges.sectorCount as number) || 0 }
              ]}
            />
          </div>

          {/* Readiness Checklist */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                AEO Generation Checklist
              </CardTitle>
              <CardDescription>
                Requirements for high-quality AEO content generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requirement</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <ChecklistRow
                    label="Products uploaded"
                    status={data.dimensions.productsUploaded.status}
                    current={`${(data.dimensions.productsUploaded.count as number) || 0} products`}
                    target="> 0"
                  />
                  <ChecklistRow
                    label="Products categorized"
                    status={data.dimensions.productsCategorized.status}
                    current={`${(data.dimensions.productsCategorized.percent as number) || 0}%`}
                    target="> 80%"
                  />
                  <ChecklistRow
                    label="Products enriched"
                    status={data.dimensions.productsEnriched.status}
                    current={`${(data.dimensions.productsEnriched.percent as number) || 0}%`}
                    target="> 70%"
                  />
                  <ChecklistRow
                    label="Buyer personas defined"
                    status={data.dimensions.buyerPersonas.status}
                    current={`${(data.dimensions.buyerPersonas.count as number) || 0} personas`}
                    target=">= 3"
                  />
                  <ChecklistRow
                    label="Question wheels evaluated"
                    status={data.dimensions.questionWheels.status}
                    current={`${(data.dimensions.questionWheels.evaluated as number) || 0} wheels`}
                    target=">= 1"
                  />
                  <ChecklistRow
                    label="Embeddings generated"
                    status={data.dimensions.embeddingsDone.status}
                    current={`${(data.dimensions.embeddingsDone.percent as number) || 0}%`}
                    target="> 90%"
                  />
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Last Updated */}
      <p className="text-xs text-muted-foreground text-center">
        Last analyzed: {new Date(data.analyzedAt).toLocaleString()}
      </p>
    </div>
  );
}

// Helper Components

interface DimensionDetailCardProps {
  title: string;
  dimension: DimensionData;
  description: string;
  details: Array<{
    label: string;
    value: number | string;
    highlight?: boolean;
  }>;
}

function DimensionDetailCard({ title, dimension, description, details }: DimensionDetailCardProps) {
  const statusColors = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600'
  };

  const bgColors = {
    green: 'bg-green-50 dark:bg-green-950/20',
    yellow: 'bg-yellow-50 dark:bg-yellow-950/20',
    red: 'bg-red-50 dark:bg-red-950/20'
  };

  return (
    <Card className={bgColors[dimension.status]}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className={`text-2xl font-bold ${statusColors[dimension.status]}`}>
            {dimension.metric}
          </span>
        </div>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Progress value={dimension.score} className="h-2 mb-3" />
        <div className="grid grid-cols-2 gap-2">
          {details.map((detail, idx) => (
            <div key={idx} className="text-sm">
              <span className="text-muted-foreground">{detail.label}: </span>
              <span className={detail.highlight && Number(detail.value) > 0 ? 'text-orange-600 font-medium' : ''}>
                {detail.value}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ChecklistRowProps {
  label: string;
  status: 'green' | 'yellow' | 'red';
  current: string;
  target: string;
}

function ChecklistRow({ label, status, current, target }: ChecklistRowProps) {
  const statusBadge = {
    green: <Badge className="bg-green-500">Pass</Badge>,
    yellow: <Badge className="bg-yellow-500">Partial</Badge>,
    red: <Badge variant="destructive">Missing</Badge>
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell>{statusBadge[status]}</TableCell>
      <TableCell className="text-right font-mono">{current}</TableCell>
      <TableCell className="text-right text-muted-foreground">{target}</TableCell>
    </TableRow>
  );
}
